import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import TopBar from './components/TopBar';
import Header from './components/Header';
import Footer from './components/Footer';
import FloatingActions from './components/FloatingActions';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Gallery from './pages/Gallery';
import Chefs from './pages/Chefs';
import RequestModal from './components/RequestModal';
import TrackModal from './components/TrackModal';
import LoginModal from './components/LoginModal';
import ManagerDashboard from './pages/ManagerDashboard';
import { ToastProvider } from './components/ToastContext';
import SearchResults from './pages/SearchResults';
import './App.css';

const AppContext = createContext();

export function useApp() {
  return useContext(AppContext);
}

export function AppProvider({ children }) {
  const [showRequest, setShowRequest] = useState(false);
  const [showTrack, setShowTrack] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [manager, setManager] = useState(null);
  const [managerLoaded, setManagerLoaded] = useState(false); // ✅ track auth load
  const [userEmail, setUserEmail] = useState(localStorage.getItem('lca_user_email') || '');

  const [preselectedPro, setPreselectedPro] = useState(null);
  const [preselectedService, setPreselectedService] = useState(null);

  useEffect(() => {
    if (userEmail) localStorage.setItem('lca_user_email', userEmail);
    else localStorage.removeItem('lca_user_email');
  }, [userEmail]);

  useEffect(() => {
    const token = localStorage.getItem('lca_token');
    if (!token) {
      setManagerLoaded(true);
      return;
    }

    axios.get('https://api.hospifylink.com/api/manager/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => setManager(r.data))
      .catch(() => localStorage.removeItem('lca_token'))
      .finally(() => setManagerLoaded(true)); // ✅ done loading
  }, []);

  const value = {
    showRequest, setShowRequest,
    showTrack, setShowTrack,
    showLogin, setShowLogin,
    manager, setManager,
    managerLoaded, // ✅ exposed to AppContent
    userEmail, setUserEmail,
    preselectedPro, setPreselectedPro,
    preselectedService, setPreselectedService
  };

  return (
    <AppContext.Provider value={value}>
      <ToastProvider>
        {children}
      </ToastProvider>
    </AppContext.Provider>
  );
}

function App() {
  return (
    <Router>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </Router>
  );
}

/* ============================================================
   Protected route — renders children only if manager is logged in
   ============================================================ */
function RequireManager({ children }) {
  const { manager, managerLoaded, setShowLogin } = useApp();

  useEffect(() => {
    if (managerLoaded && !manager) setShowLogin(true);
  }, [managerLoaded, manager, setShowLogin]);

  if (!managerLoaded) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f4f6f9',
        fontSize: 16,
        color: '#0a5c36'
      }}>
        Loading dashboard…
      </div>
    );
  }

  if (!manager) {
    // Not logged in → send back to home (login modal is shown globally)
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent() {
  const {
    showRequest, setShowRequest,
    showTrack, setShowTrack,
    showLogin, setShowLogin
  } = useApp();

  const location = useLocation();
  const isDashboard =
    location.pathname === '/manager' || location.pathname === '/dashboard';

  return (
    <>
      {/* ============ PUBLIC LAYOUT (always mounted) ============ */}
      {!isDashboard && (
        <div className="app-root">
          <TopBar />
          <Header />
          <main className="app-main">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/professionals" element={<Chefs />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <Footer />
          <FloatingActions />
        </div>
      )}

      {/* ============ DASHBOARD ROUTES (protected) ============ */}
      {isDashboard && (
        <Routes>
          <Route
            path="/manager"
            element={
              <RequireManager>
                <ManagerDashboard />
              </RequireManager>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireManager>
                <ManagerDashboard />
              </RequireManager>
            }
          />
        </Routes>
      )}

      {/* ============ GLOBAL MODALS ============ */}
      {showRequest && <RequestModal onClose={() => setShowRequest(false)} />}
      {showTrack && <TrackModal onClose={() => setShowTrack(false)} />}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </>
  );
}

export default App;